import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Input,
  Textarea,
  FormLabel,
  FormControl,
  FormErrorMessage,
  Select,
  Text,
  SimpleGrid,
  Spinner,
  useToast
} from "@chakra-ui/react";
import Card from "components/card/Card";
import { motion } from "framer-motion";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";

const InsertItemService = ({ serviceId, serviceCode, serviceName }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const imageInputRef = useRef(null);
  const [imageSize, setImageSize] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  // Define state variables for each field in the request payload.
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [translationKey, setTranslationKey] = useState("");
  const [translationIt, setTranslationIt] = useState("");
  const [translationFr, setTranslationFr] = useState("");
  const [image, setImage] = useState("");
  const [size, setSize] = useState("");
  const [HairSize, setHairSize] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [isLaundryService, setIsLaundryService] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const pageId = 13; // Page ID for global items management

  // Check if service is laundry and fetch categories
  useEffect(() => {
    const fetchCategoriesIfLaundry = async () => {
      try {
        if (serviceCode === 'UC001') {
          setIsLaundryService(true);
          const categoriesResponse = await fetchWithToken(
            `${process.env.REACT_APP_API_URL}/laundry/categories?page_id=${pageId}`,
            "GET"
          );
          if (categoriesResponse.data && categoriesResponse.data.length > 0) {
            setCategories(categoriesResponse.data);
          } else {
            toast({
              title: t("No Categories Found"),
              description: t("You need to create laundry categories before adding items."),
              status: "warning",
              duration: 5000,
              isClosable: true,
              position: "top-right",
            });
          }
        } else {
          setIsLaundryService(false);
          setCategories([]);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        setIsLaundryService(false);
        setCategories([]);
      }
    };

    if (serviceCode) {
      fetchCategoriesIfLaundry();
    }
  }, [serviceCode, pageId, t, toast]);

  // Handler for image upload; converts file to Base64 string.
  const validateImageSize = (file) => file.size <= 1024 * 1024;
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageSize(file.size);
      if (!validateImageSize(file)) {
        setFieldErrors((prev) => ({ ...prev, image: t("Image must be less than 1MB") }));
        return;
      }
      setFieldErrors((prev) => ({ ...prev, image: undefined }));
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setImage(reader.result);
      };
    } else {
      setImageSize(null);
    }
  };
// Add this inside your component, after your useState declarations

  const handleSubmit = async (event) => {
    event.preventDefault();

    // If this is a laundry service, check if category is selected
    if (isLaundryService) {
      if (categories.length === 0) {
        toast({
          title: t("No Categories Available"),
          description: t("Please create laundry categories before adding items."),
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
        return;
      }
      if (!categoryId) {
        toast({
          title: t("Category Required"),
          description: t("Please select a category for the laundry item."),
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
        return;
      }
    }

    // If this is a pet care service, hair size is required
    if (serviceCode === "UC002" && !HairSize) {
      toast({
        title: t("Hair Size Required"),
        description: t("Please select a hair size for the pet care item."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    try {
      // Construct the payload using the collected data.
      const data = {
        service_id: serviceId,
        name,
        description,
        service_name: serviceName,
        translation_key: translationKey,
        translations: { it: translationIt, fr: translationFr },
        image: image,
        size,
        ...(serviceCode === "UC002" ? { hair_size: HairSize } : {}),
        ...(isLaundryService && categoryId ? { laundry_category_id: parseInt(categoryId) } : {})
      };

      // Send a POST request to create the global item.
      await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/global-items?page_id=${pageId}`,
        "POST",
        data
      );
      // Reset the form fields.
      setName("");
      setDescription("");
      setTranslationKey("");
      setTranslationIt("");
      setTranslationFr("");
      setImage("");
      setSize("");
      setHairSize("");
      setCategoryId("");
      setError("");
      setSuccess(false);

      // Reset the file input element
      if (imageInputRef.current) {
        imageInputRef.current.value = "";
      }

      toast({
        title: t("Item added successfully!"),
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });

      // setTimeout(() => {
      //   window.location.reload();
      // }, 1000);
    } catch (error) {
      setError(error.message || t("An error occurred. Please try again later."));
      toast({
        title: t("An error occurred."),
        description: error.message || t("Please try again later."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      transition={{ duration: 0.5 }}
    >
      <Card p={{ base: "20px", md: "30px" }}>
        <form onSubmit={handleSubmit}>
          {error && (
            <Text color="#FF3B3B" textAlign="center" fontSize="lg">
              {error}
            </Text>
          )}
          {success && (
            <Text color="green.500" textAlign="center">
              {t("Item added successfully!")}
            </Text>
          )}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 2 }} spacing={6}>
            {/* Name */}
            <FormControl isRequired isInvalid={!!fieldErrors.name}>
              <FormLabel color="blue.600">{t("Name")}</FormLabel>
              <Input
                placeholder={t("Name")}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              <FormErrorMessage>{fieldErrors.name}</FormErrorMessage>
            </FormControl>
            {/* Description */}
            <FormControl isRequired isInvalid={!!fieldErrors.description}>
              <FormLabel color="blue.600">{t("Description")}</FormLabel>
              <Textarea
                placeholder={t("Description")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                minH="40px"
                size="sm"
                borderRadius={"8px"}
              />
              <FormErrorMessage>{fieldErrors.description}</FormErrorMessage>
            </FormControl>
            {/* Translation Key */}
            <FormControl isRequired isInvalid={!!fieldErrors.translationKey}>
              <FormLabel color="blue.600">{t("Translation Key")}</FormLabel>
              <Input
                placeholder={t("Translation Key")}
                type="text"
                value={translationKey}
                onChange={(e) => setTranslationKey(e.target.value)}
                required
              />
              <FormErrorMessage>{fieldErrors.translationKey}</FormErrorMessage>
            </FormControl>
            {/* Translation (French) */}
            <FormControl>
              <FormLabel color="blue.600">
                {t("Translation ")}
                <Text as="span" fontWeight="bold">
                  {t("(French)")}
                </Text>
              </FormLabel>
              <Input
                placeholder={t("Translation (French)")}
                type="text"
                value={translationFr}
                onChange={(e) => setTranslationFr(e.target.value)}
              />
            </FormControl>
            {/* Translation (Italy) */}
            <FormControl>
              <FormLabel color="blue.600">
                {t("Translation ")}
                <Text as="span" fontWeight="bold">
                  {t("(Italy)")}
                </Text>
              </FormLabel>
              <Input
                placeholder={t("Translation (Italy)")}
                type="text"
                value={translationIt}
                onChange={(e) => setTranslationIt(e.target.value)}
              />
            </FormControl>
            {/* Size */}
            <FormControl>
              <FormLabel color="blue.600">{t("Size")}</FormLabel>
              <Select
                placeholder={t("Select Size")}
                value={size}
                onChange={(e) => setSize(e.target.value)}
              >
                <option value="small">{t("Small")}</option>
                <option value="medium">{t("Medium")}</option>
                <option value="large">{t("Large")}</option>
              </Select>
            </FormControl>
            {/* Hair Size (only for pet care UC002) */}
            {serviceCode === "UC002" && (
              <FormControl isRequired>
                <FormLabel color="blue.600">{t("Hair Size")}</FormLabel>
                <Select
                  placeholder={t("Select Hair Size")}
                  value={HairSize}
                  onChange={(e) => setHairSize(e.target.value)}
                  required
                >
                  <option value="short">{t("Short")}</option>
                  <option value="long">{t("Long")}</option>
                </Select>
              </FormControl>
            )}
            {/* Laundry Category */}
            {isLaundryService && (
              <FormControl isRequired>
                <FormLabel color="blue.600">{t("Select Category")}</FormLabel>
                {categories.length === 0 ? (
                  <Box mb={4}>
                    <Text color="red.500" fontSize="sm" mb={2}>
                      {t("No categories available. Please create laundry categories first.")}
                    </Text>
                    <Select
                      placeholder={t("No categories available")}
                      isDisabled
                    />
                  </Box>
                ) : (
                  <Select
                    placeholder={t("Select Category")}
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    required
                  >
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </Select>
                )}
              </FormControl>
            )}
            {/* Image Upload */}
            <FormControl isInvalid={!!fieldErrors.image}>
              <FormLabel color="blue.600">{t("Image")}</FormLabel>
              <Input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                ref={imageInputRef}
              />
              <Text fontSize="sm" color="gray.500" mt={2}>
                {t("Recommended size: Max 1 MB (≈1024 KB), up to 1024×1024 pixels")}
              </Text>
              {imageSize !== null && (
                <Text fontSize="xs" color={imageSize > 1024 * 1024 ? "red.500" : "gray.500"} mt={1}>
                  {t("Selected file size")}: {(imageSize / 1024).toFixed(1)} KB
                  {imageSize > 1024 * 1024 && ` (${t("Too large")})`}
                </Text>
              )}
              <FormErrorMessage>{fieldErrors.image}</FormErrorMessage>
            </FormControl>
          </SimpleGrid>
          <Box>
            <Button
              mt={4}
              colorScheme="cyan"
              fontSize="m"
              borderRadius="16px"
              w={{ base: "100%", md: "148px" }}
              h="46px"
              type="submit"
              color="white"
              disabled={success}
            >
              {success ? <Spinner size="sm" /> : t("Submit")}
            </Button>
          </Box>
          {/* Show selected image preview at the bottom */}
          <Box mt={8}>
            <Text fontWeight="bold" mb={2}>{t("Selected Images")}</Text>
            <SimpleGrid columns={1} spacing={6}>
              {image && (
                <Box textAlign="center">
                  <Text fontSize="sm" mb={1}>{t("Image")}</Text>
                  <img src={image} alt="Preview" style={{ maxWidth: "100%", maxHeight: "120px", borderRadius: "8px" }} />
                </Box>
              )}
            </SimpleGrid>
          </Box>
        </form>
      </Card>
    </motion.div>
  );
};

export default InsertItemService;